import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { IoCloudUploadOutline, IoDocumentTextOutline } from "react-icons/io5";
import { Card, CardBody, CardHeader, CardTitle, Spinner } from "reactstrap";
import { toast } from "react-toastify";
import Attachment from "../../../../../../components/Attachments";
import { del, get, post, postForm } from "../../../../../../utils/helpers/api";

const MAX_FILE_SIZE_MB = 10;

/**
 * The shared Attachment component speaks the CRM `attachment_*` shape, while the
 * HR table stores `file_*` columns — so the rows are adapted on the way in.
 */
const toAttachment = (file) => ({
  id: file.id,
  attachment_title: file.file_name,
  attachment_path: file.file_path,
  attachment_type: file.file_type,
  attachment_size: file.file_size
});

function FilesTab({ employeeId, onCountChange }) {
  const { t } = useTranslation();
  const fileInputRef = useRef(null);
  const [files, setFiles] = useState([]);
  const [page, setPage] = useState(1);
  const [lastPage, setLastPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(0);
  const [dragging, setDragging] = useState(false);
  const [error, setError] = useState(false);

  // Kept in a ref so the callback identity never re-triggers the fetch effect.
  const onCountChangeRef = useRef(onCountChange);
  onCountChangeRef.current = onCountChange;

  const getFiles = useCallback(async (targetPage = 1) => {
    if (!employeeId) return;

    setLoading(true);

    const data = await get(`/hr/employees/${employeeId}/files?page=${targetPage}`);

    if (data?.success) {
      setFiles(prev =>
        targetPage === 1 ? data.files.data : [...prev, ...data.files.data]
      );
      setPage(data.files.current_page);
      setLastPage(data.files.last_page);
      onCountChangeRef.current?.(data.files.total);
      setError(false);
    } else {
      setError(true);
    }

    setLoading(false);
  }, [employeeId]);

  useEffect(() => {
    getFiles(1);
  }, [getFiles]);

  const uploadFiles = async (selected) => {
    const accepted = Array.from(selected).filter(file => {
      if (file.size > MAX_FILE_SIZE_MB * 1024 * 1024) {
        toast.error(t('hr.fileTooLarge', { name: file.name, size: MAX_FILE_SIZE_MB }));
        return false;
      }
      return true;
    });

    if (!accepted.length) return;

    setUploading(accepted.length);

    for (const file of accepted) {
      const body = new FormData();
      body.append('file', file);

      try {
        const data = await postForm(`/hr/employees/${employeeId}/files/store`, body);
        if (!data?.success) throw new Error();
      } catch {
        toast.error(t('hr.uploadFailed', { name: file.name }));
      }

      setUploading(prev => prev - 1);
    }

    getFiles(1);
  };

  const handleFileChange = (event) => {
    uploadFiles(event.target.files);
    event.target.value = '';
  };

  const handleDrop = (event) => {
    event.preventDefault();
    setDragging(false);
    if (event.dataTransfer.files?.length) uploadFiles(event.dataTransfer.files);
  };

  const deleteFile = async (id) => {
    const data = await del('/hr/employees/files/delete/' + id);
    if (data?.success) getFiles(1);
  };

  const renameFile = async (id, name) => {
    const data = await post('/hr/employees/files/update/' + id, { file_name: name });
    if (data?.success) getFiles(1);
  };

  return (
    <Card style={{ maxWidth: 760 }}>
      <CardHeader className="d-flex justify-content-between align-items-center">
        <CardTitle>
          <IoDocumentTextOutline size={18} /> {t('hr.documentsAndFiles')}
        </CardTitle>
        <button
          type="button"
          className="emp-link-btn"
          disabled={uploading > 0}
          onClick={() => fileInputRef.current?.click()}
        >
          <IoCloudUploadOutline size={14} /> {t('common.uploadFile')}
        </button>
      </CardHeader>
      <CardBody>
        <input
          type="file"
          multiple
          className="d-none"
          ref={fileInputRef}
          onChange={handleFileChange}
        />

        <div
          className="emp-upload mb-3"
          style={dragging ? { borderColor: 'var(--nazar-primary)', background: '#faf9ff' } : undefined}
          onClick={() => fileInputRef.current?.click()}
          onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
          onDragLeave={() => setDragging(false)}
          onDrop={handleDrop}
        >
          {uploading > 0 ?
            <>
              <Spinner size="sm" color="primary" />
              {t('hr.uploadingFiles', { count: uploading })}
            </>
          :
            <>
              <IoCloudUploadOutline size={22} />
              {t('hr.dropFilesHere')}
              <span className="fw-normal" style={{ fontSize: '0.75rem' }}>
                {t('hr.maxFileSize', { size: MAX_FILE_SIZE_MB })}
              </span>
            </>
          }
        </div>

        {loading && files.length === 0 ?
          <Spinner color="primary" className="d-block mx-auto my-4" />
        : error ?
          <div className="emp-empty">{t('hr.failedLoadFiles')}</div>
        : files.length === 0 ?
          <div className="emp-empty">{t('hr.noFiles')}</div>
        :
          <>
            {files.map(file => (
              <Attachment
                key={file.id}
                attachment={toAttachment(file)}
                onDelete={deleteFile}
                onRename={renameFile}
              />
            ))}

            {page < lastPage &&
              <div className="text-center pt-2">
                <button
                  type="button"
                  className="emp-btn"
                  disabled={loading}
                  onClick={() => getFiles(page + 1)}
                >
                  {loading ? t('common.loading') : t('hr.loadMore')}
                </button>
              </div>
            }
          </>
        }
      </CardBody>
    </Card>
  );
}

export default FilesTab;
